import React, { useState, useEffect, useCallback } from 'react';
import { bankAnalyticsAPI, investorsAPI, expenseTypesAPI, propertyAPI, vendorAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Upload, 
  FileSpreadsheet, 
  Search, 
  Filter, 
  Download, 
  Trash2, 
  Edit, 
  Check, 
  X, 
  Loader2,
  TrendingUp,
  TrendingDown,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  PieChart,
  BarChart3,
  Building2,
  Users,
  CreditCard,
  Calendar,
  RefreshCw,
  Eye,
  CheckCircle,
  AlertCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  Plus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';

const COLORS = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#14b8a6', '#f97316', '#84cc16'];

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return dateStr;
};

export const BankAnalyticsPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  
  // Upload state
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  
  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [transactionsPage, setTransactionsPage] = useState(0);
  const [transactionsLimit] = useState(50);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  
  // Sorting state for transactions
  const [sortField, setSortField] = useState('transaction_date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Dashboard state
  const [dashboard, setDashboard] = useState(null);
  
  // Filters state
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    dr_cr: '',
    property_name: '',
    expense_category: '',
    expense_type: '',
    vendor_name: '',
    investor_name: '',
    payment_type: '',
    batch_id: '',
    start_date: '',
    end_date: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    properties: [],
    expense_categories: [],
    expense_types: [],
    vendors: [],
    investors: [],
    payment_types: [],
    batches: []
  });
  
  // Masters state
  const [investors, setInvestors] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  
  // Add new master states
  const [newInvestorName, setNewInvestorName] = useState('');
  const [newExpenseTypeName, setNewExpenseTypeName] = useState('');
  const [addingInvestor, setAddingInvestor] = useState(false);
  const [addingExpenseType, setAddingExpenseType] = useState(false);
  const [properties, setProperties] = useState([]);
  const [vendors, setVendors] = useState([]);

  // Fetch data on mount
  useEffect(() => {
    fetchUploads();
    fetchFilters();
    fetchDashboard();
    fetchMasters();
  }, []);

  // Fetch transactions when filters or page changes
  useEffect(() => {
    if (activeTab === 'transactions') {
      fetchTransactions();
    }
  }, [activeTab, transactionsPage, filters, sortField, sortDirection]);

  const fetchMasters = async () => {
    try {
      const [propsRes, vendorsRes] = await Promise.all([
        propertyAPI.getAll({ status: 'active' }).catch(() => ({ data: [] })),
        vendorAPI.getAll({ status: 'active' }).catch(() => ({ data: [] }))
      ]);
      setProperties(propsRes.data || []);
      setVendors(vendorsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch masters:', error);
    }
  };

  const fetchUploads = async () => {
    try {
      const response = await bankAnalyticsAPI.getUploads();
      setUploads(response.data || []);
    } catch (error) {
      console.error('Failed to fetch uploads:', error);
    }
  };

  const fetchFilters = async () => {
    try {
      const response = await bankAnalyticsAPI.getFilters();
      setFilterOptions(response.data || {});
    } catch (error) {
      console.error('Failed to fetch filters:', error);
    }
  };

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.property_name) params.property_name = filters.property_name;
      if (filters.batch_id) params.batch_id = filters.batch_id;
      
      const response = await bankAnalyticsAPI.getDashboard(params);
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = {
        skip: transactionsPage * transactionsLimit,
        limit: transactionsLimit,
        sort_by: sortField,
        sort_order: sortDirection
      };
      
      // Add filters
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params[key] = filters[key];
        }
      });
      
      const response = await bankAnalyticsAPI.getTransactions(params);
      setTransactions(response.data.transactions || []);
      setTransactionsTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  // Handle column sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // File upload handler
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploading(true);
      const response = await bankAnalyticsAPI.uploadStatement(file);
      setUploadPreview(response.data);
      toast.success('File uploaded successfully! Review and confirm import.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Confirm import
  const handleConfirmImport = async () => {
    if (!uploadPreview) return;
    
    try {
      setImporting(true);
      const response = await bankAnalyticsAPI.importStatement(
        uploadPreview.upload_id,
        uploadPreview.detected_mapping,
        true
      );
      
      toast.success(`Imported ${response.data.imported_records} transactions. ${response.data.duplicate_records} duplicates skipped.`);
      setUploadPreview(null);
      fetchUploads();
      fetchFilters();
      fetchDashboard();
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import data');
    } finally {
      setImporting(false);
    }
  };

  // Delete upload
  const handleDeleteUpload = async (uploadId) => {
    if (!confirm('Delete this upload and all associated transactions?')) return;
    
    try {
      await bankAnalyticsAPI.deleteUpload(uploadId);
      toast.success('Upload deleted');
      fetchUploads();
      fetchFilters();
      fetchDashboard();
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to delete upload');
    }
  };

  // Update transaction
  const handleUpdateTransaction = async (data) => {
    if (!editingTransaction) return;
    
    try {
      await bankAnalyticsAPI.updateTransaction(editingTransaction.id, data);
      toast.success('Transaction updated');
      setEditingTransaction(null);
      fetchTransactions();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to update transaction');
    }
  };

  // Bulk update
  const handleBulkUpdate = async (data) => {
    if (selectedTransactions.length === 0) return;
    
    try {
      await bankAnalyticsAPI.bulkUpdateTransactions(selectedTransactions, data);
      toast.success(`Updated ${selectedTransactions.length} transactions`);
      setSelectedTransactions([]);
      setBulkEditOpen(false);
      fetchTransactions();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to update transactions');
    }
  };

  // Export
  const handleExport = async (format = 'csv') => {
    try {
      const params = { format };
      Object.keys(filters).forEach(key => {
        if (filters[key]) params[key] = filters[key];
      });
      
      const response = await bankAnalyticsAPI.exportTransactions(params);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bank_transactions.${format === 'excel' ? 'xlsx' : 'csv'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  // Add new investor
  const handleAddInvestor = async () => {
    if (!newInvestorName.trim()) {
      toast.error('Please enter investor name');
      return;
    }
    try {
      setAddingInvestor(true);
      await investorsAPI.create({ name: newInvestorName.trim() });
      toast.success('Investor added successfully');
      setNewInvestorName('');
      fetchFilters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add investor');
    } finally {
      setAddingInvestor(false);
    }
  };

  // Add new expense type
  const handleAddExpenseType = async () => {
    if (!newExpenseTypeName.trim()) {
      toast.error('Please enter expense type name');
      return;
    }
    try {
      setAddingExpenseType(true);
      await expenseTypesAPI.create({ name: newExpenseTypeName.trim() });
      toast.success('Expense type added successfully');
      setNewExpenseTypeName('');
      fetchFilters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add expense type');
    } finally {
      setAddingExpenseType(false);
    }
  };

  // Toggle transaction selection
  const toggleTransactionSelection = (id) => {
    setSelectedTransactions(prev => 
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  // Select all transactions
  const toggleSelectAll = () => {
    if (selectedTransactions.length === transactions.length) {
      setSelectedTransactions([]);
    } else {
      setSelectedTransactions(transactions.map(t => t.id));
    }
  };

  // Apply filter
  const applyFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setTransactionsPage(0);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      dr_cr: '',
      property_name: '',
      expense_category: '',
      expense_type: '',
      vendor_name: '',
      investor_name: '',
      payment_type: '',
      batch_id: '',
      start_date: '',
      end_date: ''
    });
    setTransactionsPage(0);
  };

  // Refresh dashboard
  const refreshDashboard = () => {
    fetchDashboard();
    fetchFilters();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Bank Statement Analytics</h1>
          <p className="text-muted-foreground">Upload, categorize, and analyze bank transactions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleExport('csv')}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={() => handleExport('excel')}>
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="h-4 w-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">
            <CreditCard className="h-4 w-4 mr-2" />
            Transactions
          </TabsTrigger>
          <TabsTrigger value="upload" data-testid="tab-upload">
            <Upload className="h-4 w-4 mr-2" />
            Upload
          </TabsTrigger>
          <TabsTrigger value="masters" data-testid="tab-masters">
            <Users className="h-4 w-4 mr-2" />
            Masters
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Dashboard Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <Label>Start Date</Label>
                  <Input 
                    type="date" 
                    value={filters.start_date}
                    onChange={(e) => applyFilter('start_date', e.target.value)}
                    className="w-40"
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input 
                    type="date" 
                    value={filters.end_date}
                    onChange={(e) => applyFilter('end_date', e.target.value)}
                    className="w-40"
                  />
                </div>
                <div>
                  <Label>Property</Label>
                  <Select value={filters.property_name || "all"} onValueChange={(v) => applyFilter('property_name', v === "all" ? "" : v)}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="All Properties" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Properties</SelectItem>
                      {filterOptions.properties?.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Import Batch</Label>
                  <Select value={filters.batch_id || "all"} onValueChange={(v) => applyFilter('batch_id', v === "all" ? "" : v)}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="All Batches" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Batches</SelectItem>
                      {filterOptions.batches?.map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.original_file_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={refreshDashboard} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
                <Button onClick={clearFilters} variant="ghost">
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : dashboard ? (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600 font-medium">Total Inflow</p>
                        <p className="text-2xl font-bold text-green-700">{formatCurrency(dashboard.summary.total_inflow)}</p>
                      </div>
                      <div className="p-3 bg-green-200 rounded-full">
                        <ArrowDownRight className="h-6 w-6 text-green-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-red-600 font-medium">Total Outflow</p>
                        <p className="text-2xl font-bold text-red-700">{formatCurrency(dashboard.summary.total_outflow)}</p>
                      </div>
                      <div className="p-3 bg-red-200 rounded-full">
                        <ArrowUpRight className="h-6 w-6 text-red-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600 font-medium">Net Balance</p>
                        <p className="text-2xl font-bold text-blue-700">{formatCurrency(dashboard.summary.net_balance)}</p>
                      </div>
                      <div className="p-3 bg-blue-200 rounded-full">
                        <Wallet className="h-6 w-6 text-blue-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-600 font-medium">Total Transactions</p>
                        <p className="text-2xl font-bold text-purple-700">{dashboard.summary.total_transactions?.toLocaleString()}</p>
                      </div>
                      <div className="p-3 bg-purple-200 rounded-full">
                        <CreditCard className="h-6 w-6 text-purple-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Status Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-yellow-100 rounded-lg">
                        <AlertCircle className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Uncategorized</p>
                        <p className="text-xl font-bold">{dashboard.summary.uncategorized?.toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Clock className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Categorized</p>
                        <p className="text-xl font-bold">{dashboard.summary.categorized?.toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Reviewed</p>
                        <p className="text-xl font-bold">{dashboard.summary.reviewed?.toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sweep Info Card */}
              {dashboard.summary.sweep_transactions > 0 && (
                <Card className="bg-gray-50 border-gray-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-200 rounded-lg">
                          <RefreshCw className="h-5 w-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Sweep Transactions (Excluded from Inflow/Outflow)</p>
                          <p className="text-xl font-bold">{dashboard.summary.sweep_transactions?.toLocaleString()} transactions</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Total Sweep Amount</p>
                        <p className="text-lg font-semibold text-gray-600">{formatCurrency(dashboard.summary.sweep_total)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Charts Row 1 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Monthly Trend */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Monthly Trend
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={Object.entries(dashboard.monthly_trend || {}).map(([month, data]) => ({
                        month: month.substring(0, 7),
                        inflow: data.inflow,
                        outflow: data.outflow
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                        <Legend />
                        <Line type="monotone" dataKey="inflow" stroke="#10b981" strokeWidth={2} name="Inflow" />
                        <Line type="monotone" dataKey="outflow" stroke="#ef4444" strokeWidth={2} name="Outflow" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Category Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="h-5 w-5" />
                      Expense Category Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <RechartsPie>
                        <Pie
                          data={Object.entries(dashboard.category_breakdown || {}).map(([name, value]) => ({ name, value }))}
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name.substring(0, 15)}${name.length > 15 ? '...' : ''} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {Object.entries(dashboard.category_breakdown || {}).map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row 2 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Property Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5" />
                      Property-wise Spend
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={Object.entries(dashboard.property_breakdown || {}).map(([name, value]) => ({ name, value }))} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
                        <YAxis dataKey="name" type="category" width={100} />
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                        <Bar dataKey="value" fill="#8b5cf6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Top Vendors */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Top Vendors
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={Object.entries(dashboard.vendor_breakdown || {}).slice(0, 10).map(([name, value]) => ({ name: name.substring(0, 20), value }))} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
                        <YAxis dataKey="name" type="category" width={120} />
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                        <Bar dataKey="value" fill="#06b6d4" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Expense Type Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Top Expense Types
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={Object.entries(dashboard.expense_type_breakdown || {}).map(([name, value]) => ({ name: name.substring(0, 25), value }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} interval={0} />
                      <YAxis tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
                      <Tooltip formatter={(v) => formatCurrency(v)} />
                      <Bar dataKey="value" fill="#f59e0b" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No data available. Upload a bank statement to get started.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                  <Label>Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input 
                      placeholder="Search description, reference, vendor..."
                      value={filters.search}
                      onChange={(e) => applyFilter('search', e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={filters.status || "all"} onValueChange={(v) => applyFilter('status', v === "all" ? "" : v)}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="uncategorized">Uncategorized</SelectItem>
                      <SelectItem value="categorized">Categorized</SelectItem>
                      <SelectItem value="reviewed">Reviewed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={filters.dr_cr || "all"} onValueChange={(v) => applyFilter('dr_cr', v === "all" ? "" : v)}>
                    <SelectTrigger className="w-28">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="DR">Debit (DR)</SelectItem>
                      <SelectItem value="CR">Credit (CR)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Property</Label>
                  <Select value={filters.property_name || "all"} onValueChange={(v) => applyFilter('property_name', v === "all" ? "" : v)}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Properties</SelectItem>
                      {filterOptions.properties?.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={filters.expense_category || "all"} onValueChange={(v) => applyFilter('expense_category', v === "all" ? "" : v)}>
                    <SelectTrigger className="w-44">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {filterOptions.expense_categories?.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="ghost" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Bulk Actions */}
          {selectedTransactions.length > 0 && (
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{selectedTransactions.length} transactions selected</span>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => setBulkEditOpen(true)}>
                      <Edit className="h-4 w-4 mr-1" />
                      Bulk Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setSelectedTransactions([])}>
                      Clear Selection
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Transactions Table */}
          <Card>
            <CardContent className="pt-6">
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox 
                            checked={selectedTransactions.length === transactions.length && transactions.length > 0}
                            onCheckedChange={toggleSelectAll}
                          />
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleSort('transaction_date')}
                        >
                          <div className="flex items-center gap-1">
                            Date
                            {sortField === 'transaction_date' ? (
                              sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                            ) : <ArrowUpDown className="h-3 w-3 opacity-50" />}
                          </div>
                        </TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead 
                          className="text-right cursor-pointer hover:bg-muted/50"
                          onClick={() => handleSort('amount')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            Amount
                            {sortField === 'amount' ? (
                              sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                            ) : <ArrowUpDown className="h-3 w-3 opacity-50" />}
                          </div>
                        </TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleSort('expense_type')}
                        >
                          <div className="flex items-center gap-1">
                            Expense Type
                            {sortField === 'expense_type' ? (
                              sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                            ) : <ArrowUpDown className="h-3 w-3 opacity-50" />}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleSort('expense_category')}
                        >
                          <div className="flex items-center gap-1">
                            Category
                            {sortField === 'expense_category' ? (
                              sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                            ) : <ArrowUpDown className="h-3 w-3 opacity-50" />}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleSort('property_name')}
                        >
                          <div className="flex items-center gap-1">
                            Property
                            {sortField === 'property_name' ? (
                              sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                            ) : <ArrowUpDown className="h-3 w-3 opacity-50" />}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleSort('status')}
                        >
                          <div className="flex items-center gap-1">
                            Status
                            {sortField === 'status' ? (
                              sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                            ) : <ArrowUpDown className="h-3 w-3 opacity-50" />}
                          </div>
                        </TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={11} className="text-center text-muted-foreground py-8">
                            No transactions found
                          </TableCell>
                        </TableRow>
                      ) : (
                        transactions.map(tx => (
                          <TableRow key={tx.id} className={tx.status === 'uncategorized' ? 'bg-yellow-50' : ''}>
                            <TableCell>
                              <Checkbox 
                                checked={selectedTransactions.includes(tx.id)}
                                onCheckedChange={() => toggleTransactionSelection(tx.id)}
                              />
                            </TableCell>
                            <TableCell className="whitespace-nowrap">{formatDate(tx.transaction_date)}</TableCell>
                            <TableCell className="max-w-[200px] truncate" title={tx.description}>
                              {tx.description || '-'}
                            </TableCell>
                            <TableCell className="text-xs">{tx.reference_number || '-'}</TableCell>
                            <TableCell className={`text-right font-medium ${tx.dr_cr === 'DR' ? 'text-red-600' : 'text-green-600'}`}>
                              {formatCurrency(tx.amount)}
                            </TableCell>
                            <TableCell>
                              <Badge variant={tx.dr_cr === 'DR' ? 'destructive' : 'default'} className="text-xs">
                                {tx.dr_cr}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm">{tx.expense_type || '-'}</TableCell>
                            <TableCell className="text-sm">{tx.expense_category || '-'}</TableCell>
                            <TableCell className="text-sm">{tx.property_name || '-'}</TableCell>
                            <TableCell>
                              <Badge variant={
                                tx.status === 'reviewed' ? 'default' :
                                tx.status === 'categorized' ? 'secondary' : 'outline'
                              } className="text-xs">
                                {tx.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Button size="sm" variant="ghost" onClick={() => setEditingTransaction(tx)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      Showing {transactionsPage * transactionsLimit + 1} to {Math.min((transactionsPage + 1) * transactionsLimit, transactionsTotal)} of {transactionsTotal} transactions
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setTransactionsPage(p => Math.max(0, p - 1))}
                        disabled={transactionsPage === 0}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setTransactionsPage(p => p + 1)}
                        disabled={(transactionsPage + 1) * transactionsLimit >= transactionsTotal}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Upload Tab */}
        <TabsContent value="upload" className="space-y-6">
          {/* Upload Zone */}
          <Card>
            <CardHeader>
              <CardTitle>Upload Bank Statement</CardTitle>
              <CardDescription>Upload Excel (.xlsx, .xls) or CSV files</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                  disabled={uploading}
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  {uploading ? (
                    <Loader2 className="h-12 w-12 mx-auto text-muted-foreground animate-spin" />
                  ) : (
                    <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                  )}
                  <p className="mt-4 text-lg font-medium">
                    {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Excel or CSV files up to 10MB
                  </p>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Upload Preview */}
          {uploadPreview && (
            <Card className="border-primary">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5" />
                  Preview: {uploadPreview.file_name}
                </CardTitle>
                <CardDescription>{uploadPreview.total_records} records found</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <h4 className="font-medium mb-2">Detected Columns:</h4>
                  <div className="flex flex-wrap gap-2">
                    {uploadPreview.columns?.map(col => (
                      <Badge key={col.original_name} variant="outline">
                        {col.original_name} → {uploadPreview.detected_mapping?.[col.original_name] || 'unmapped'}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="mb-4">
                  <h4 className="font-medium mb-2">Preview Data:</h4>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {uploadPreview.columns?.slice(0, 8).map(col => (
                            <TableHead key={col.original_name} className="text-xs whitespace-nowrap">
                              {col.original_name}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {uploadPreview.preview_data?.slice(0, 5).map((row, idx) => (
                          <TableRow key={idx}>
                            {uploadPreview.columns?.slice(0, 8).map(col => (
                              <TableCell key={col.original_name} className="text-xs">
                                {String(row[col.original_name] || '').substring(0, 30)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button onClick={handleConfirmImport} disabled={importing}>
                    {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                    {importing ? 'Importing...' : 'Confirm Import'}
                  </Button>
                  <Button variant="outline" onClick={() => setUploadPreview(null)}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upload History */}
          <Card>
            <CardHeader>
              <CardTitle>Upload History</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>File Name</TableHead>
                    <TableHead>Upload Date</TableHead>
                    <TableHead>Uploaded By</TableHead>
                    <TableHead>Records</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {uploads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No uploads yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    uploads.map(upload => (
                      <TableRow key={upload.id}>
                        <TableCell className="font-medium">{upload.original_file_name}</TableCell>
                        <TableCell>{new Date(upload.upload_timestamp).toLocaleDateString()}</TableCell>
                        <TableCell>{upload.uploaded_by_name}</TableCell>
                        <TableCell>
                          {upload.imported_records || upload.total_records}
                          {upload.duplicate_records > 0 && (
                            <span className="text-xs text-muted-foreground ml-1">
                              ({upload.duplicate_records} dupe)
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={upload.status === 'completed' ? 'default' : 'secondary'}>
                            {upload.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-600"
                            onClick={() => handleDeleteUpload(upload.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Masters Tab */}
        <TabsContent value="masters" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Investors */}
            <Card>
              <CardHeader>
                <CardTitle>Investors</CardTitle>
                <CardDescription>Manage investor names for transaction classification</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  <Input 
                    placeholder="Enter new investor name"
                    value={newInvestorName}
                    onChange={(e) => setNewInvestorName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddInvestor()}
                    data-testid="new-investor-input"
                  />
                  <Button onClick={handleAddInvestor} disabled={addingInvestor} data-testid="add-investor-btn">
                    {addingInvestor ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {filterOptions.investors?.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No investors yet. Add one above or they will be auto-created during import.
                    </p>
                  ) : (
                    filterOptions.investors?.map(inv => (
                      <div key={inv} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                        <span>{inv}</span>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Expense Types */}
            <Card>
              <CardHeader>
                <CardTitle>Expense Types</CardTitle>
                <CardDescription>Manage expense types for transaction classification</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  <Input 
                    placeholder="Enter new expense type"
                    value={newExpenseTypeName}
                    onChange={(e) => setNewExpenseTypeName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddExpenseType()}
                    data-testid="new-expense-type-input"
                  />
                  <Button onClick={handleAddExpenseType} disabled={addingExpenseType} data-testid="add-expense-type-btn">
                    {addingExpenseType ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {filterOptions.expense_types?.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No expense types yet. Add one above or they will be auto-created during import.
                    </p>
                  ) : (
                    filterOptions.expense_types?.slice(0, 50).map(et => (
                      <div key={et} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                        <span className="text-sm">{et}</span>
                      </div>
                    ))
                  )}
                  {filterOptions.expense_types?.length > 50 && (
                    <p className="text-xs text-muted-foreground text-center">
                      + {filterOptions.expense_types.length - 50} more
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Transaction Dialog */}
      <Dialog open={!!editingTransaction} onOpenChange={() => setEditingTransaction(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Transaction</DialogTitle>
            <DialogDescription>
              Update transaction classification
            </DialogDescription>
          </DialogHeader>
          {editingTransaction && (
            <TransactionEditForm 
              transaction={editingTransaction}
              filterOptions={filterOptions}
              onSave={handleUpdateTransaction}
              onCancel={() => setEditingTransaction(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={bulkEditOpen} onOpenChange={setBulkEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Edit {selectedTransactions.length} Transactions</DialogTitle>
            <DialogDescription>
              Apply changes to all selected transactions
            </DialogDescription>
          </DialogHeader>
          <TransactionEditForm 
            transaction={{}}
            filterOptions={filterOptions}
            onSave={handleBulkUpdate}
            onCancel={() => setBulkEditOpen(false)}
            isBulk={true}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Transaction Edit Form Component
const TransactionEditForm = ({ transaction, filterOptions, onSave, onCancel, isBulk = false }) => {
  const [formData, setFormData] = useState({
    expense_type: transaction.expense_type || '',
    vendor_name: transaction.vendor_name || '',
    expense_category: transaction.expense_category || '',
    investor_name: transaction.investor_name || '',
    property_name: transaction.property_name || '',
    payment_type: transaction.payment_type || '',
    customer_name: transaction.customer_name || '',
    remarks: transaction.remarks || '',
    notes: transaction.notes || '',
    status: transaction.status || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Only include non-empty values for bulk edit
    const data = isBulk 
      ? Object.fromEntries(Object.entries(formData).filter(([_, v]) => v))
      : formData;
    onSave(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!isBulk && transaction.description && (
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm"><strong>Date:</strong> {transaction.transaction_date}</p>
          <p className="text-sm"><strong>Description:</strong> {transaction.description}</p>
          <p className="text-sm"><strong>Amount:</strong> {formatCurrency(transaction.amount)} ({transaction.dr_cr})</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Expense Type</Label>
          <Select value={formData.expense_type || "none"} onValueChange={(v) => setFormData(prev => ({ ...prev, expense_type: v === "none" ? "" : v }))}>
            <SelectTrigger>
              <SelectValue placeholder="Select expense type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- None --</SelectItem>
              {filterOptions.expense_types?.map(et => (
                <SelectItem key={et} value={et}>{et}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Expense Category</Label>
          <Select value={formData.expense_category || "none"} onValueChange={(v) => setFormData(prev => ({ ...prev, expense_category: v === "none" ? "" : v }))}>
            <SelectTrigger>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- None --</SelectItem>
              {filterOptions.expense_categories?.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Property</Label>
          <Select value={formData.property_name || "none"} onValueChange={(v) => setFormData(prev => ({ ...prev, property_name: v === "none" ? "" : v }))}>
            <SelectTrigger>
              <SelectValue placeholder="Select property" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- None --</SelectItem>
              {filterOptions.properties?.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Investor</Label>
          <Select value={formData.investor_name || "none"} onValueChange={(v) => setFormData(prev => ({ ...prev, investor_name: v === "none" ? "" : v }))}>
            <SelectTrigger>
              <SelectValue placeholder="Select investor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- None --</SelectItem>
              {filterOptions.investors?.map(i => (
                <SelectItem key={i} value={i}>{i}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Payment Type</Label>
          <Select value={formData.payment_type || "none"} onValueChange={(v) => setFormData(prev => ({ ...prev, payment_type: v === "none" ? "" : v }))}>
            <SelectTrigger>
              <SelectValue placeholder="Select payment type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- None --</SelectItem>
              {filterOptions.payment_types?.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Vendor Name</Label>
          <Input 
            value={formData.vendor_name}
            onChange={(e) => setFormData(prev => ({ ...prev, vendor_name: e.target.value }))}
            placeholder="Enter vendor name"
          />
        </div>

        <div>
          <Label>Customer Name</Label>
          <Input 
            value={formData.customer_name}
            onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
            placeholder="Enter customer name"
          />
        </div>

        <div>
          <Label>Status</Label>
          <Select value={formData.status || "keep"} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v === "keep" ? "" : v }))}>
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="keep">-- Keep Current --</SelectItem>
              <SelectItem value="uncategorized">Uncategorized</SelectItem>
              <SelectItem value="categorized">Categorized</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Remarks</Label>
        <Input 
          value={formData.remarks}
          onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
          placeholder="Enter remarks"
        />
      </div>

      <div>
        <Label>Internal Notes</Label>
        <Textarea 
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          placeholder="Add internal notes..."
          rows={2}
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          <Check className="h-4 w-4 mr-2" />
          {isBulk ? 'Update All' : 'Save Changes'}
        </Button>
      </DialogFooter>
    </form>
  );
};

export default BankAnalyticsPage;
